import { FormInstance } from 'antd';
import { Form } from '@common/components';
import { Store } from 'antd/lib/form/interface';
import React, { useMemo } from 'react';

import { useMutation } from '@common/hooks';
import { datetime, formatToYearMonthDay } from '@common/services/datetime';
import { ComplianceItemFilesListV2 } from '@onboarding/components/compliance-item-files-list';
import { ComplianceItemInputExpirationDate } from '@onboarding/components/compliance-item-input-expiration-date';
import { ComplianceItemV2Type } from '@onboarding/graphql';
import { UpdateDocumentMutation } from 'works/legacy/graphql/document';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { createMessage } from 'works/legacy/lib/message-helper';

type Props = {
  form: FormInstance;
  complianceItem: ComplianceItemV2Type;
  refetchQueries: string[];
  onDeleted?: () => void;
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

export function ComplianceItemUpdateFormOtherDoc({
  form,
  complianceItem,
  refetchQueries,
  onDeleted,
  onSubmit,
  onSuccess,
  onError,
}: Props) {
  const { detailItem } = complianceItem;

  const errorMessage = () =>
    createMessage('error', 'Failed to update document. Please try again.');

  const [updateDocument, { loading: formSubmitting }] = useMutation(
    UpdateDocumentMutation,
    {
      refetchQueries,
      onCompleted: (response) => {
        if (response?.updateDocument.errors) {
          addErrorsToForm(form, response.updateDocument.errors);
          errorMessage();
          onError?.();
        } else {
          onSuccess?.();
          createMessage('success', 'Document has been successfully updated.');
        }
      },
      onError: () => {
        errorMessage();

        onError?.();
      },
    }
  );

  const initialValues = useMemo(
    () => ({
      docType: detailItem.docType,
      completedOn: detailItem.completedOn && datetime(detailItem.completedOn),
      expiresOn: detailItem.expiresOn && datetime(detailItem.expiresOn),
    }),
    [detailItem]
  );

  if (!detailItem) {
    return null;
  }

  const onFinish = (values: Store) => {
    if (formSubmitting) return;

    onSubmit?.();

    updateDocument({
      variables: {
        id: complianceItem.id,
        input: {
          completedOn: formatToYearMonthDay(values.completedOn),
          expiresOn: formatToYearMonthDay(values.expiresOn),
        },
        files: values.files?.fileList ?? [],
      },
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={initialValues}
    >
      <ComplianceItemFilesListV2
        refetchQueries={refetchQueries}
        complianceItemId={complianceItem.id}
        complianceItemStateApprovedAt={complianceItem.stateApprovedAt}
        attachmentList={detailItem.detailItemAttachments}
        onDeleted={onDeleted}
        formContent={
          <ComplianceItemInputExpirationDate
            expirationType={complianceItem.expirationType}
          />
        }
      />
    </Form>
  );
}
